import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import AppHeader from '../Header/AppHeader';
import './AccountSettings.css';

function AccountSettings() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      
      if (formData.name !== user.name) {
        updateData.name = formData.name;
      }
      
      if (formData.email !== user.email) {
        updateData.email = formData.email;
      }

      if (Object.keys(updateData).length > 0) {
        await apiClient.updateProfile(updateData);
        toast.success(isGerman ? '✅ Profil erfolgreich aktualisiert' : '✅ Profile updated successfully');
      } else {
        toast.info(isGerman ? 'ℹ️ Keine Änderungen vorgenommen' : 'ℹ️ No changes made');
      }
    } catch (error) {
      toast.error(isGerman ? '❌ Fehler beim Aktualisieren des Profils' : '❌ Error updating profile', {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(isGerman ? '❌ Passwörter stimmen nicht überein' : '❌ Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error(isGerman ? '❌ Passwort muss mindestens 6 Zeichen lang sein' : '❌ Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await apiClient.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });
      
      toast.success(isGerman ? '✅ Passwort erfolgreich geändert' : '✅ Password changed successfully');
      
      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(isGerman ? '❌ Fehler beim Ändern des Passworts' : '❌ Error changing password', {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppHeader />
      <div className="account-settings">
        <div className="account-container">
          <div className="account-header">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="btn-back"
              data-testid="back-button"
            >
              ← {isGerman ? 'Zurück zum Dashboard' : 'Back to Dashboard'}
            </button>
            <h1 className="account-title">{t('account.title')}</h1>
          </div>

          {/* Submenu Navigation */}
          <div className="account-submenu">
            <button 
              className="submenu-item active"
              onClick={() => navigate('/account')}
            >
              {isGerman ? 'Profil bearbeiten' : 'Edit Profile'}
            </button>
            <button 
              className="submenu-item"
              onClick={() => navigate('/subscription')}
            >
              {isGerman ? 'Mein Abo' : 'My Subscription'}
            </button>
            <button 
              className="submenu-item"
              onClick={() => navigate('/settings')}
            >
              {t('common.settings')}
            </button>
          </div>

          {/* Profile Section */}
          <div className="account-section">
            <h2 className="section-title">{isGerman ? 'Persönliche Informationen' : 'Personal Information'}</h2>
            <form onSubmit={handleUpdateProfile} className="account-form">
              <div className="form-group">
                <label htmlFor="name">{isGerman ? 'Vorname' : 'First Name'}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('auth.login.email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="email-input"
                />
              </div>

              <button 
                type="submit" 
                className="btn-save"
                disabled={loading}
                data-testid="save-profile-button"
              >
                {loading 
                  ? (isGerman ? 'Speichert...' : 'Saving...') 
                  : (isGerman ? 'Änderungen speichern' : 'Save Changes')}
              </button>
            </form>
          </div>

          {/* Password Section */}
          <div className="account-section">
            <h2 className="section-title">{t('account.profile.changePassword')}</h2>
            <form onSubmit={handleChangePassword} className="account-form">
              <div className="form-group">
                <label htmlFor="currentPassword">{t('account.profile.currentPassword')}</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="current-password-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">{t('account.profile.newPassword')}</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="new-password-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">{t('account.profile.confirmPassword')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="confirm-password-input"
                />
              </div>

              <button 
                type="submit" 
                className="btn-save"
                disabled={loading}
                data-testid="change-password-button"
              >
                {loading 
                  ? (isGerman ? 'Ändert...' : 'Changing...') 
                  : t('account.profile.changePassword')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AccountSettings;
